import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Button } from '@truecare/ui';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { adminBeforeLoad } from '../lib/admin-before-load';
import { TotpReEnrollModal } from '../components/TotpReEnrollModal';

/**
 * 2FA setup gate. Every /admin/* endpoint requires an operator with TOTP
 * enrolled + satisfied, so a brand-new operator (magic-link signed in, no 2FA
 * yet) is routed here by adminBeforeLoad instead of hitting "Failed to load"
 * on every page. Reuses adminBeforeLoad for the shared auth + operator-only
 * checks (passing its own path so the 2FA redirect doesn't loop), then bounces
 * an already-enrolled operator home.
 */
export const Route = createFileRoute('/setup-2fa')({
  beforeLoad: async () => {
    // Shared gate: not-authed → sign-in; non-operator → portal; the 2FA redirect
    // is skipped for this exact path so it doesn't recurse.
    await adminBeforeLoad('/setup-2fa');
    const me = (await unwrap(api.GET('/api/v1/auth/me'))) as {
      totpEnrolled?: boolean;
    };
    // Already enrolled → no gate needed.
    if (me.totpEnrolled === true) throw redirect({ to: '/' });
  },
  component: Setup2faPage,
});

function Setup2faPage() {
  const navigate = useNavigate();
  const [enrolling, setEnrolling] = useState(false);

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <div className="w-full max-w-md">
        <h1 className="font-heading text-3xl text-forest md:text-4xl">
          Set up two-factor authentication
        </h1>
        <p className="mt-3 font-body text-sm text-ink-muted">
          Admin access requires 2FA on your account. Set it up now to continue —
          you'll scan a QR code with an authenticator app (Google Authenticator,
          1Password, Authy, etc.) and enter a 6-digit code to confirm.
        </p>
        <div className="mt-6">
          <Button variant="primary" onClick={() => setEnrolling(true)}>
            Set up 2FA
          </Button>
        </div>

        {enrolling && (
          <TotpReEnrollModal
            isReEnrollment={false}
            onClose={() => {
              setEnrolling(false);
              // Re-check after the modal closes: if enrollment completed, the
              // session is now TOTP-satisfied → proceed into the admin.
              void (async () => {
                try {
                  const me = (await unwrap(api.GET('/api/v1/auth/me'))) as {
                    totpEnrolled?: boolean;
                  };
                  if (me.totpEnrolled === true) await navigate({ to: '/' });
                } catch {
                  // stay on the gate; the operator can retry
                }
              })();
            }}
          />
        )}
      </div>
    </div>
  );
}
